<template>
  <div>
    <pageHeader/>
    <video ref="video" autoplay playsinline></video>
    <canvas ref="canvas" width="640" height="480"></canvas>
    <div v-if="isPoseDetected" class="alert">
      检测到目标姿势！已截图。
    </div>
  </div>
</template>

<script>
import * as tf from '@tensorflow/tfjs';
import * as poseDetection from '@tensorflow-models/pose-detection';

export default {
  name: 'PoseDetection',
  data() {
    return {
      detector: null,
      video: null,
      canvas: null,
      ctx: null,
      isPoseDetected: false,
      isFrontCaptured: false, // 是否已经拍摄正面照
    };
  },
  async mounted() {
    await this.setupCamera();
    this.detector = await poseDetection.createDetector(poseDetection.SupportedModels.MoveNet);
    this.ctx = this.$refs.canvas.getContext('2d');
    this.detectPose();
  },
  methods: {
    // 设置摄像头
    async setupCamera() {
      this.video = this.$refs.video;
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      this.video.srcObject = stream;
      return new Promise((resolve) => {
        this.video.onloadedmetadata = () => {
          resolve();
        };
      });
    },

    // 检测姿势
    async detectPose() {
      const poses = await this.detector.estimatePoses(this.video);
      this.ctx.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);
      this.ctx.drawImage(this.video, 0, 0, this.$refs.canvas.width, this.$refs.canvas.height);

      if (poses.length > 0) {
        const pose = poses[0];
        this.drawKeypoints(pose.keypoints);

        // 判断是否满足条件
        if (!this.isFrontCaptured) {
          // 检测正面照
          if (this.checkFrontPose(pose.keypoints)) {
            this.isPoseDetected = true;
            this.captureImage('front');
            this.isFrontCaptured = true;
          }
        } else {
          // 检测侧面照
          if (this.checkSidePose(pose.keypoints)) {
            this.isPoseDetected = true;
            this.captureImage('side');
            this.isFrontCaptured = false; // 重置状态，以便下次拍摄
          }
        }
      }

      requestAnimationFrame(this.detectPose);
    },

    // 绘制关键点
    drawKeypoints(keypoints) {
      keypoints.forEach((keypoint) => {
        if (keypoint.score > 0.5) {
          this.ctx.beginPath();
          this.ctx.arc(keypoint.x, keypoint.y, 5, 0, 2 * Math.PI);
          this.ctx.fillStyle = 'red';
          this.ctx.fill();
        }
      });
    },

    // 判断正面姿势是否满足条件
    checkFrontPose(keypoints) {
      const leftShoulder = keypoints.find((kp) => kp.name === 'left_shoulder');
      const rightShoulder = keypoints.find((kp) => kp.name === 'right_shoulder');
      const leftElbow = keypoints.find((kp) => kp.name === 'left_elbow');
      const rightElbow = keypoints.find((kp) => kp.name === 'right_elbow');

      if (!leftShoulder || !rightShoulder || !leftElbow || !rightElbow) {
        return false;
      }

      // 计算双臂的角度
      const leftArmAngle = this.calculateAngle(leftShoulder, leftElbow);
      const rightArmAngle = this.calculateAngle(rightShoulder, rightElbow);

      // 判断双臂是否张开约 45 度
      const isArmsOpen =
        Math.abs(leftArmAngle - 45) < 10 && Math.abs(rightArmAngle - 45) < 10;

      // 判断是否正面站立（肩部水平线倾斜）
      const isFrontFacing =
        Math.abs(leftShoulder.y - rightShoulder.y) < 20;

      return isArmsOpen && isFrontFacing;
    },

    // 判断侧面姿势是否满足条件
    checkSidePose(keypoints) {
      const leftShoulder = keypoints.find((kp) => kp.name === 'left_shoulder');
      const rightShoulder = keypoints.find((kp) => kp.name === 'right_shoulder');

      if (!leftShoulder || !rightShoulder) {
        return false;
      }

      // 判断是否侧面站立（肩部水平线倾斜较大）
      const isSideFacing =
        Math.abs(leftShoulder.y - rightShoulder.y) > 50;

      return isSideFacing;
    },

    // 计算两点之间的角度
    calculateAngle(pointA, pointB) {
      const dx = pointB.x - pointA.x;
      const dy = pointB.y - pointA.y;
      // 计算两点之间的连线与垂直线的夹角
      return Math.abs((Math.atan2(dx, dy) * 180) / Math.PI);
    },

    // 截图
    captureImage(type) {
      const image = this.$refs.canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `pose_capture_${type}.png`;
      link.click();
    },
  },
};
</script>

<style scoped>
video, canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover; /* 确保视频覆盖整个屏幕 */
    }
.alert {
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0, 255, 0, 0.7);
  padding: 10px;
  color: white;
  font-size: 16px;
}
</style>